import * as XLSX from "xlsx";
import { getEmployees } from "../Employees/EmployeeApi";
import { listOffboardings } from "../Lifecycle/lifecycleApi";
import { downloadBlob } from "../Employees/employeeFileUtils";

export function employeeFullName(emp) {
  return (
    String(emp?.display_name || "").trim() ||
    [emp?.first_name, emp?.middle_name, emp?.last_name].filter(Boolean).join(" ").trim() ||
    "—"
  );
}

export function departmentLabel(emp) {
  return emp?.department?.name || emp?.department_name || "—";
}

export function designationLabel(emp) {
  return emp?.designation?.title || emp?.designation_title || "—";
}

export function employeePhone(emp) {
  return emp?.mobile_phone || emp?.phone || "—";
}

export function employeeEmail(emp) {
  return emp?.email || emp?.work_email || "—";
}

export function employeeStatusLabel(emp) {
  if (emp?.status) return emp.status;
  return emp?.is_active === false ? "Inactive" : "Active";
}

export function toIsoDate(value) {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

export function monthBounds(date = new Date()) {
  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return { from: toIsoDate(start), to: toIsoDate(end) };
}

export function priorMonthBounds(date = new Date()) {
  const start = new Date(date.getFullYear(), date.getMonth() - 1, 1);
  const end = new Date(date.getFullYear(), date.getMonth(), 0);
  return { from: toIsoDate(start), to: toIsoDate(end) };
}

export function dateInRange(value, from, to) {
  const iso = toIsoDate(value);
  if (!iso) return false;
  if (from && iso < from) return false;
  if (to && iso > to) return false;
  return true;
}

export async function fetchAllEmployees(options = {}) {
  const data = await getEmployees({
    department_id: options.departmentId || undefined,
    designation_id: options.designationId || undefined,
    active_only: options.activeOnly,
  });
  return Array.isArray(data) ? data : data?.items || [];
}

async function fetchAllOffboardings() {
  const all = [];
  let page = 1;
  let total = 0;
  do {
    const res = await listOffboardings({ page, page_size: 100 });
    all.push(...(res.items || []));
    total = res.total ?? all.length;
    page += 1;
  } while (all.length < total && page <= 50);
  return all;
}

export function employeeDepartmentId(emp) {
  const id = emp?.department_id ?? emp?.department?.department_id ?? "";
  return id ? String(id) : "";
}

export function employeeDesignationId(emp) {
  const id = emp?.designation_id ?? emp?.designation?.designation_id ?? "";
  return id ? String(id) : "";
}

export function applyMasterFilters(employees, filters) {
  return employees.filter((emp) => {
    if (filters.employeeId && String(emp.employee_id) !== String(filters.employeeId)) return false;
    if (filters.departmentId && employeeDepartmentId(emp) !== String(filters.departmentId)) return false;
    if (filters.designationId && employeeDesignationId(emp) !== String(filters.designationId)) return false;
    if (filters.status === "active" && emp.is_active === false) return false;
    if (filters.status === "inactive" && emp.is_active !== false) return false;
    return true;
  });
}

export function applyDirectoryFilters(employees, filters) {
  return employees.filter((emp) => {
    if (filters.departmentId && employeeDepartmentId(emp) !== String(filters.departmentId)) return false;
    if (filters.designationId && employeeDesignationId(emp) !== String(filters.designationId)) return false;
    return true;
  });
}

export function applyJoiningFilters(employees, filters) {
  return employees.filter((emp) => {
    if (filters.departmentId && employeeDepartmentId(emp) !== String(filters.departmentId)) return false;
    return dateInRange(emp.date_of_joining, filters.fromDate, filters.toDate);
  });
}

export async function buildExitReportRows(filters) {
  const [employees, offboardings] = await Promise.all([
    fetchAllEmployees({ activeOnly: false }),
    fetchAllOffboardings(),
  ]);

  const rows = [];
  const seen = new Set();

  const byId = new Map(employees.map((e) => [String(e.employee_id), e]));
  // Optional branch / employee-type scope from the report filters.
  const matches = (emp) => !filters.matchesEmployee || (emp && filters.matchesEmployee(emp));

  for (const off of offboardings) {
    const lwd = off.last_working_day || off.resignation_date;
    if (!dateInRange(lwd, filters.fromDate, filters.toDate)) continue;
    if (!matches(byId.get(String(off.employee_id)) || off.employee)) continue;
    const key = String(off.employee_id || off.offboarding_id);
    if (seen.has(key)) continue;
    seen.add(key);
    rows.push({
      employee: off.employee_name || employeeFullName(off.employee) || "—",
      department: off.employee ? departmentLabel(off.employee) : "—",
      exitDate: lwd ? toIsoDate(lwd) : "—",
      reason: off.reason_for_exit || off.separation_type || "Resigned",
      type: "Resigned",
      status: "Resigned",
    });
  }

  for (const emp of employees) {
    if (emp.is_active !== false) continue;
    if (!matches(emp)) continue;
    const exitDate = emp.date_of_exit || emp.last_working_day || emp.updated_at;
    if (!dateInRange(exitDate, filters.fromDate, filters.toDate)) continue;
    const key = String(emp.employee_id);
    if (seen.has(key)) continue;
    seen.add(key);
    rows.push({
      employee: employeeFullName(emp),
      department: departmentLabel(emp),
      exitDate: exitDate ? toIsoDate(exitDate) : "—",
      reason: emp.status || "Inactive",
      type: "Inactive",
      status: "Inactive",
    });
  }

  return rows.sort((a, b) => String(b.exitDate).localeCompare(String(a.exitDate)));
}

export function masterReportRows(employees) {
  return employees.map((emp) => ({
    "Employee Code": emp.employee_code || "—",
    "Employee Name": employeeFullName(emp),
    Department: departmentLabel(emp),
    Designation: designationLabel(emp),
    DOJ: emp.date_of_joining ? toIsoDate(emp.date_of_joining) : "—",
    Status: employeeStatusLabel(emp),
  }));
}

export function directoryReportRows(employees) {
  return employees.map((emp) => ({
    "Employee Name": employeeFullName(emp),
    Mobile: employeePhone(emp),
    Email: employeeEmail(emp),
    Department: departmentLabel(emp),
    Designation: designationLabel(emp),
  }));
}

export function joiningReportRows(employees) {
  return employees.map((emp) => ({
    "Employee Code": emp.employee_code || "—",
    "Employee Name": employeeFullName(emp),
    Department: departmentLabel(emp),
    Designation: designationLabel(emp),
    "Joining Date": emp.date_of_joining ? toIsoDate(emp.date_of_joining) : "—",
    DOJ: emp.date_of_joining ? toIsoDate(emp.date_of_joining) : "—",
    Status: employeeStatusLabel(emp),
  }));
}

export function joiningReportPreviewRows(employees) {
  return joiningReportRows(employees).map(({ "Employee Name": name, Department, Designation, "Joining Date": joiningDate }) => ({
    "Employee Name": name,
    Department,
    Designation,
    "Joining Date": joiningDate,
  }));
}

export function computeWorkforceKpis(employees) {
  const { from, to } = monthBounds();
  const list = Array.isArray(employees) ? employees : [];
  return {
    total: list.length,
    active: list.filter((e) => e.is_active !== false).length,
    inactive: list.filter((e) => e.is_active === false).length,
    newJoiners: list.filter((e) => dateInRange(e.date_of_joining, from, to)).length,
  };
}

export function exitReportExportRows(rows) {
  return rows.map((row) => ({
    "Employee Name": row.employee,
    Department: row.department || "—",
    "Exit Date": row.exitDate,
    "Exit Reason": row.reason,
    Status: row.status || row.type || "—",
    Type: row.type,
  }));
}

export function exitReportPreviewRows(rows) {
  return exitReportExportRows(rows);
}

export function exportRowsToXlsx(rows, sheetName, filename) {
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(rows.length ? rows : [{ Message: "No records found" }]);
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  downloadBlob(
    new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    filename
  );
}
